import {
  AlertCircle,
  ArrowUpRight,
  CheckCircle,
  ChevronRight,
  Clock,
  PawPrint,
  XCircle,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { generatePath, Link } from 'react-router-dom'

import { formatInstantDate, resolveDateFormat } from '@snootsvet/shared'
import type { PublicInvoice } from '@snootsvet/shared/portal'
import { formatCurrency } from '@snootsvet/shared/portal'

import { useRegion } from '@/customer/hooks/use-region'
import { routes } from '@/customer/routes'

const INVOICE_STYLES = {
  paid: { bg: 'bg-green', text: 'text-green', icon: CheckCircle, label: 'Paid' },
  open: { bg: 'bg-yellow', text: 'text-foreground', icon: Clock, label: 'Open' },
  pendingUpgrade: {
    bg: 'bg-yellow',
    text: 'text-foreground',
    icon: ArrowUpRight,
    label: 'Upgrade pending',
  },
  void: { bg: 'bg-neutral-400', text: 'text-muted-foreground', icon: XCircle, label: 'Void' },
  uncollectible: {
    bg: 'bg-neutral-400',
    text: 'text-muted-foreground',
    icon: XCircle,
    label: 'Uncollectible',
  },
  overdue: { bg: 'bg-warning', text: 'text-warning', icon: AlertCircle, label: 'Overdue' },
}

function invoiceStyle(invoice: PublicInvoice) {
  if (invoice.overdue) return INVOICE_STYLES.overdue
  if (invoice.pendingSubscriptionUpdate) return INVOICE_STYLES.pendingUpgrade
  return INVOICE_STYLES[invoice.status]
}

function compactSummary(invoice: PublicInvoice): string {
  if (invoice.source === 'subscription') return invoice.description ?? invoice.number ?? ''
  const items = invoice.lineItems.filter((item) => item.kind === 'item')
  const first = items[0]?.description
  if (!first) return invoice.description ?? invoice.number ?? ''
  const remaining = items.length - 1
  return remaining > 0 ? `${first} + ${remaining} more` : first
}

export function CompactInvoiceCard({
  invoice,
  petName,
}: {
  invoice: PublicInvoice
  petName: string
}) {
  const region = useRegion()
  const style = invoiceStyle(invoice)
  const StatusIcon = style.icon
  const displayDate = invoice.issuedAt

  return (
    <div className="flex overflow-hidden rounded-lg border-2 border-foreground">
      <div className={`w-2 shrink-0 ${style.bg}`} />
      <div className="flex flex-1 flex-col">
        <Link
          to={generatePath(routes.account.billing.invoice, { invoiceId: invoice.id })}
          className="flex items-center gap-2 bg-neutral-200 px-4 py-2.5 transition-opacity hover:opacity-80"
        >
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5">
            <span className="text-button-sm">
              {formatInstantDate(displayDate, resolveDateFormat(null, region.id))}
            </span>
            {petName && (
              <span className="flex items-center gap-1 text-button-sm">
                <PawPrint className="size-3.5" />
                {petName}
              </span>
            )}
          </div>
          <span className={`ml-auto flex shrink-0 items-center gap-1 text-button-sm ${style.text}`}>
            <StatusIcon className="size-3.5" />
            {style.label}
          </span>
          <ChevronRight className="size-4 shrink-0" />
        </Link>

        <div className="flex items-center justify-between gap-4 bg-background px-4 py-2.5">
          <div className="flex min-w-0 flex-col gap-1 text-label-sm text-muted-foreground">
            <span className="line-clamp-2">{compactSummary(invoice)}</span>
            {invoice.status === 'open' && invoice.dueDate && (
              <span>
                Due {formatInstantDate(invoice.dueDate, resolveDateFormat(null, region.id))}
              </span>
            )}
          </div>
          <span className="shrink-0 text-label-sm font-bold tabular-nums">
            {formatCurrency(invoice.amountDue, invoice.currency)}
          </span>
        </div>
      </div>
    </div>
  )
}

export function InvoiceCard({
  invoice,
  petName,
  action,
}: {
  invoice: PublicInvoice
  petName: string
  action?: ReactNode
}) {
  const region = useRegion()
  const style = invoiceStyle(invoice)
  const StatusIcon = style.icon
  const displayDate = invoice.issuedAt

  return (
    <div className="flex overflow-hidden rounded-lg border-2 border-foreground">
      <div className={`w-2 shrink-0 ${style.bg}`} />
      <div className="flex flex-1 flex-col">
        <div className="flex items-center gap-2 bg-neutral-200 px-4 py-2.5">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5">
            <span className="text-button-sm">
              {formatInstantDate(displayDate, resolveDateFormat(null, region.id))}
            </span>
            {petName && (
              <span className="flex items-center gap-1 text-button-sm">
                <PawPrint className="size-3.5" />
                {petName}
              </span>
            )}
          </div>
          <span className={`ml-auto flex shrink-0 items-center gap-1 text-button-sm ${style.text}`}>
            <StatusIcon className="size-3.5" />
            {style.label}
          </span>
        </div>

        <div className="flex flex-col gap-2 bg-muted px-4 py-3">
          <div className="text-label-sm">{invoice.description ?? invoice.number}</div>
          {invoice.status === 'open' && invoice.dueDate && (
            <div className="text-label-sm text-muted-foreground">
              Due {formatInstantDate(invoice.dueDate, resolveDateFormat(null, region.id))}
            </div>
          )}

          {invoice.lineItems.length > 0 && (
            <ul className="flex flex-col gap-1 border-t border-foreground/10 pt-2 text-label-sm">
              {invoice.lineItems.map((item) => (
                <li
                  key={item.description}
                  className="flex items-baseline justify-between gap-4 pl-3 -indent-3"
                >
                  <span className="min-w-0 text-muted-foreground">
                    <span className="mr-1.5 inline-block indent-0 text-[6px] align-middle opacity-40">
                      ●
                    </span>
                    {item.description}
                  </span>
                  <span className="shrink-0 tabular-nums">
                    {formatCurrency(item.amount, invoice.currency)}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {(action || invoice.lineItems.length > 0) && (
            <div className="flex items-center justify-between gap-4 border-t border-foreground/10 pt-2">
              <div>{action}</div>
              <span className="text-label-sm font-bold tabular-nums">
                {formatCurrency(invoice.amountDue, invoice.currency)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
